import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import PendingApprovalsPanel from './approveComponents/PendingApprovalsPanel';
import ApprovalsListView from './approveComponents/ApprovalsListView';
import PageToolbar from './util/PageToolbar';
import SearchFilter, { ActiveFilter } from './util/SearchFilter';
import ViewToggle from './util/ViewToggle';
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { ColDef } from './util/DataTable';

interface Props {
  access: any;
  refreshUserHeder: any;
  staticDateCompany: any;
}

const ApproveNew: React.FC<Props> = ({ refreshUserHeder, access }) => {
  const [reportPending, setReportsPending] = useState<any[]>([]);
  const [userId, setUserID]               = useState<any>();
  const [rows, setRows]                   = useState<any[]>([]);
  const [loading, setLoading]             = useState(false);
  const [search, setSearch]               = useState('');
  const [panelView, setPanelView]         = useState<'split' | 'list'>('split');
  const [allUsers, setAllUsers]           = useState<any[]>([]);
  const [filters, setFilters]             = useState<ActiveFilter[]>([]);
  const auth    = useSelector((state: RootState) => state.auth.value);
  const location = useLocation();

  // Колонки-фильтры для встроенного фильтра SearchFilter (сотрудник → select)
  const filterColumns: ColDef[] = React.useMemo(() => [{
    key: 'userName',
    label: t('Employee'),
    render: () => null,               // фильтр-only колонка, в таблице не рендерится
    filterType: 'select',
    filterOptions: allUsers
      .map((u: any) => { const n = `${u.first_name} ${u.last_name}`.trim(); return { value: n, label: n }; })
      .sort((a, b) => a.label.localeCompare(b.label)),
  }], [allUsers]);

  const buildRows = useCallback((data: any[]) =>
    data.map(item => {
      const diff = (() => {
        let d = moment(item.endTime).diff(moment(item.startTime), 'minutes');
        if (d < 0) d = moment(item.endTime).add(1, 'day').diff(moment(item.startTime), 'minutes');
        return Math.ceil(d / 5) * 5;
      })();
      return {
        userId:       item.userId,
        date:         item.date,
        startTime:    item.startTime,
        endTime:      item.endTime,
        categoryid:   item.categoryId,
        description:  item.description,
        dateId:       item.id,
        categoryTree: String(item.categoryTree ?? ''),
        category:     String(item.category?.name ?? ''),
        userName:     `${item.user?.first_name ?? ''} ${item.user?.last_name ?? ''}`.trim(),
        superVisor:   item.supervisor
          ? `${item.supervisor.first_name} ${item.supervisor.last_name}`.trim()
          : '',
        tasks:       item.tasks,
        can_approve: item.can_approve,
        allTime:     `${Math.floor(diff / 60)}:${(diff % 60).toString().padStart(2, '0')}`,
      };
    })
  , []);

  const updateRowsAfterStatusChange = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`time-reports/${userId}`);
      setReportsPending(data);
    } catch { /* handled globally */ }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: me }, { data: users }] = await Promise.all([
          axios.get('user'),
          axios.get('users'),
        ]);
        setUserID(me.id);
        const { data: pending } = await axios.get(`time-reports/${me.id}`);
        setReportsPending(pending);
        setAllUsers(users || []);
      } catch { /* handled globally */ }
      finally { setLoading(false); }
    })();
  }, [location.pathname]);

  useEffect(() => {
    setRows(buildRows(reportPending));
  }, [reportPending, buildRows]);

  // ?user=ID (dashboard "Open in Approvals") preselects the employee filter once users are loaded
  const presetUser = useRef(new URLSearchParams(location.search).get('user'));
  useEffect(() => {
    const id = Number(presetUser.current);
    if (!id || !allUsers.length) return;
    const u = allUsers.find((x: any) => x.id === id);
    presetUser.current = null;
    if (!u) return;
    const name = `${u.first_name} ${u.last_name}`.trim();
    setFilters([{ colKey: 'userName', label: t('Employee') as string, value: name }]);
  }, [allUsers]);

  if (!auth)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );

  // Combined search string (text search + employee filter из SearchFilter)
  const employeeFilter = filters.find(f => f.colKey === 'userName');
  const effectiveSearch = employeeFilter ? employeeFilter.value : search;

  return (
    <>
      {/* ── PageToolbar ── */}
      <PageToolbar mb='var(--space-l1)'>
          <SearchFilter
            grow
            columns={filterColumns}
            search={search}
            onSearch={setSearch}
            filters={filters}
            onFilters={setFilters}
          />
          <div style={{ flex: 1 }} />
          <ViewToggle
            value={panelView}
            onChange={v => setPanelView(v as 'split' | 'list')}
            options={[
              { value: 'split', label: 'Split', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="3" width="8" height="18" rx="1"/><rect x="13" y="3" width="8" height="18" rx="1"/></svg> },
              { value: 'list',  label: 'List',  icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
            ]}
          />
      </PageToolbar>

      {/* ── Main panel — section-scroll fills remaining TabPanel space ── */}
      <div className="dashboard-card w-full flex flex-col section-scroll"
        style={{ overflowY: panelView === 'list' ? 'auto' : 'hidden' }}>
        {panelView === 'split' ? (
          <PendingApprovalsPanel
            rows={rows}
            updateRowsAfterStatusChange={updateRowsAfterStatusChange}
            setLoading={setLoading}
            loading={loading}
            refreshUserHeder={refreshUserHeder}
            search={effectiveSearch}
            canReturnWeek={Boolean(access?.timesheet_return_week)}
          />
        ) : (
          <ApprovalsListView
            rows={rows}
            search={effectiveSearch}
            updateRowsAfterStatusChange={updateRowsAfterStatusChange}
            setLoading={setLoading}
            loading={loading}
            refreshUserHeder={refreshUserHeder}
          />
        )}
      </div>
    </>
  );
};

export default ApproveNew;
